import { ChatBotSettingsFormValues } from "@jugl-web/domain-resources/chat-bot";
import { useUpdateChatBotSettings } from "@jugl-web/domain-resources/chat-bot/hooks/useUpdateChatBotSettings";
import { transformChatBotSettingsToFormValues } from "@jugl-web/domain-resources/chat-bot/utils/transformChatBotSettingsToFormValues";
import { useNavigationGuardContext } from "@jugl-web/domain-resources/common/providers/NavigationGuardProvider";
import { LoadingSpinner, MultiSectionLayout } from "@jugl-web/ui-components";
import { Button } from "@jugl-web/ui-kit/primitives/Button";
import { useToast, useTranslations } from "@jugl-web/utils";
import { useFormSubmittingState } from "@jugl-web/utils/form/hooks/useFormSubmittingState";
import { useWebChatBotAccount } from "@web-src/modules/client-support/pages/ClientSupportPage/providers/WebChatBotAccountProvider/useWebChatBotAccount";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { FC, ReactNode, useCallback, useEffect, useMemo } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import {
  ChatBotChannelPreview,
  ChatBotChannelPreviewMode,
} from "../ChatBotChannelPreview";
import { ChatbotPreviewBackground } from "../ChatbotPreviewBackground";
import { ReactComponent as ArrowRightIcon } from "./assets/arrow-right.svg";

const INITIAL_FORM_VALUES: ChatBotSettingsFormValues = {
  chatName: "Support",
  agentName: "Agent",
  welcomeMessage: "Hello! How can I help you?",
  senderMessageBackgroundColor: "#1A75D2",
  theme: "light",
  buttonColor: "#1A75D2",
  alignment: "right",
  bottomSpacing: 20,
  sideSpacing: 20,
  chatImage: "",
  agentImage: "default-1",
  buttonIcon: "default-1",
  ctas: [],
  suggestions: [],
  ctaVisual: null,
};

interface AppearanceSettingsTabShellProps {
  tabId: string;
  previewMode: ChatBotChannelPreviewMode;
  children: ReactNode;
}

export const AppearanceSettingsTabShell: FC<
  AppearanceSettingsTabShellProps
> = ({ tabId, previewMode, children }) => {
  const { t } = useTranslations();
  const { toast } = useToast();
  const { entityId } = useEntitySelectedProvider();

  const { requestNavigation, registerDirtySource } =
    useNavigationGuardContext();

  const { webChatBotAccount } = useWebChatBotAccount();

  const { updateChatBotSettings } = useUpdateChatBotSettings({ entityId });

  const { isSubmitting, setIsSubmitting } = useFormSubmittingState();

  const form = useForm<ChatBotSettingsFormValues>({
    defaultValues: webChatBotAccount
      ? transformChatBotSettingsToFormValues(webChatBotAccount.settings)
      : INITIAL_FORM_VALUES,
  });

  const { handleSubmit, reset, control, formState } = form;

  const resetForm = useCallback(() => {
    if (!webChatBotAccount) {
      return;
    }

    reset(transformChatBotSettingsToFormValues(webChatBotAccount.settings));
  }, [reset, webChatBotAccount]);

  const watchedValues = useWatch({ control });

  const previewCtas = useMemo(() => {
    const sourceCtas =
      (watchedValues.ctas as { id: string; value: string }[]) ?? [];

    return sourceCtas.filter((cta) => cta.value.trim().length > 0);
  }, [watchedValues.ctas]);

  const previewSuggestions = useMemo(() => {
    const sourceSuggestions =
      (watchedValues.suggestions as { id: string; value: string }[]) ?? [];

    return sourceSuggestions.filter(
      (suggestion) => suggestion.value.trim().length > 0
    );
  }, [watchedValues.suggestions]);

  const handleSaveChanges = useCallback(
    async (formData: ChatBotSettingsFormValues) => {
      const removeEmptyItems = (
        items: { id: string; value: string }[]
      ): string[] =>
        items.map((item) => item.value.trim()).filter((v) => v.length > 0);

      try {
        setIsSubmitting(true);

        await updateChatBotSettings({
          welcomeMessage: formData.welcomeMessage.trim(),
          agentName: formData.agentName.trim(),
          agentImage: formData.agentImage,
          chatName: formData.chatName.trim(),
          chatImage: formData.chatImage,
          buttonIcon: formData.buttonIcon,
          buttonColor: formData.buttonColor,
          senderMessageBackgroundColor: formData.senderMessageBackgroundColor,
          theme: formData.theme,
          alignment: formData.alignment,
          bottomSpacing: formData.bottomSpacing,
          sideSpacing: formData.sideSpacing,
          ctas: removeEmptyItems(formData.ctas),
          ctaVisual: formData.ctaVisual,
          suggestions: removeEmptyItems(formData.suggestions),
        });

        toast(
          t({
            id: "feedback.web-chat-settings-saved",
            defaultMessage: "Web Chat settings have been updated",
          })
        );
      } catch {
        toast(
          t({
            id: "feedback.something-went-wrong",
            defaultMessage: "Something went wrong",
          }),
          { variant: "error" }
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [setIsSubmitting, t, toast, updateChatBotSettings]
  );

  // Reset the form when the component mounts/settings change
  useEffect(() => {
    resetForm();
  }, [resetForm]);

  useEffect(() => {
    return registerDirtySource(tabId, formState.isDirty);
  }, [formState.isDirty, registerDirtySource, tabId]);

  return (
    <>
      <MultiSectionLayout
        className="animate-fade-in"
        classNames={{
          content: "flex items-center justify-center",
        }}
        leftSide={
          <MultiSectionLayout
            classNames={{
              content:
                "border-grey-300 flex h-full w-[464px] shrink-0 flex-col border-0 border-r border-solid bg-white",
            }}
            footer={
              formState.isDirty && (
                <div className="flex gap-4 border-t border-r border-b-0 border-l-0 border-solid border-grey-300 bg-white px-8 pt-4 pb-6">
                  <Button
                    variant="tertiary"
                    size="md"
                    className="flex-1"
                    onClick={() => requestNavigation(resetForm)}
                  >
                    {t({
                      id: "common.cancel",
                      defaultMessage: "Cancel",
                    })}
                  </Button>
                  <Button
                    variant="primary"
                    size="md"
                    className="flex-[2_1_0%]"
                    onClick={handleSubmit(handleSaveChanges)}
                    isDisabled={isSubmitting}
                    iconEnd={
                      isSubmitting ? (
                        <LoadingSpinner size="xs" />
                      ) : (
                        <ArrowRightIcon />
                      )
                    }
                  >
                    {t({
                      id: "common.save-changes",
                      defaultMessage: "Save changes",
                    })}
                  </Button>
                </div>
              )
            }
          >
            <FormProvider {...form}>
              <div className="jugl__custom-scrollbar flex h-full flex-col overflow-y-auto px-6 py-10">
                {children}
              </div>
            </FormProvider>
          </MultiSectionLayout>
        }
      >
        <ChatbotPreviewBackground>
          <ChatBotChannelPreview
            entityId={entityId}
            chatName={watchedValues.chatName ?? INITIAL_FORM_VALUES.chatName}
            agentName={watchedValues.agentName ?? INITIAL_FORM_VALUES.agentName}
            welcomeMessage={
              watchedValues.welcomeMessage ?? INITIAL_FORM_VALUES.welcomeMessage
            }
            senderMessageBackgroundColor={
              watchedValues.senderMessageBackgroundColor ??
              INITIAL_FORM_VALUES.senderMessageBackgroundColor
            }
            theme={watchedValues.theme ?? INITIAL_FORM_VALUES.theme}
            buttonColor={
              watchedValues.buttonColor ?? INITIAL_FORM_VALUES.buttonColor
            }
            alignment={watchedValues.alignment ?? INITIAL_FORM_VALUES.alignment}
            bottomSpacing={
              watchedValues.bottomSpacing ?? INITIAL_FORM_VALUES.bottomSpacing
            }
            sideSpacing={
              watchedValues.sideSpacing ?? INITIAL_FORM_VALUES.sideSpacing
            }
            chatImage={watchedValues.chatImage ?? INITIAL_FORM_VALUES.chatImage}
            agentImage={
              watchedValues.agentImage ?? INITIAL_FORM_VALUES.agentImage
            }
            buttonIcon={
              watchedValues.buttonIcon ?? INITIAL_FORM_VALUES.buttonIcon
            }
            ctaVisual={watchedValues.ctaVisual ?? INITIAL_FORM_VALUES.ctaVisual}
            ctas={previewCtas}
            suggestions={previewSuggestions}
            chatMode="mocked"
            previewMode={previewMode}
          />
        </ChatbotPreviewBackground>
      </MultiSectionLayout>
    </>
  );
};
